import { Component } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import {
  Tenant,
  TenantsService,
  UpdateTenantRequest,
} from 'src/app/services/tenants.service';
import { states } from 'src/app/utils/state-list';

@Component({
  selector: 'app-tenant-info-setup-form',
  templateUrl: './tenant-info-setup-form.component.html',
  styleUrls: ['./tenant-info-setup-form.component.scss'],
})
export class TenantInfoSetupFormComponent {
  first: string = '';
  last: string = '';
  title: string = '';
  company: string = '';

  address: string = '';
  address2: string = '';
  city: string = '';
  st: string = '';
  zip: string = '';

  billingSameAsAddress: boolean = false;
  billingAddress: string = '';
  billingAddress2: string = '';
  billingCity: string = '';
  billingSt: string = '';
  billingZip: string = '';

  phone1: string = '';
  phone2: string = '';
  emails: string[] = [];
  billingEmails: string[] = [];

  newEmail: string = '';
  newBillingEmail: string = '';

  phone1Error: string = '';
  phone2Error: string = '';
  emailError: string = '';
  billingEmailError: string = '';

  states = states;

  currentStep: number = 1;

  loadingTenant = false;
  tenant?: Tenant;
  tenantFetchError: string = '';
  error = '';

  constructor(
    private authService: AuthService,
    private tenantsService: TenantsService
  ) {
    this.getTenantDetails();
  }

  getTenantDetails() {
    this.loadingTenant = true;
    this.tenantsService.getCurrentTenantDetails().subscribe({
      next: (tenant) => {
        this.loadingTenant = false;
        this.tenant = tenant.tenant;
        this.initializeData(this.tenant);
      },
      error: (err) => {
        this.loadingTenant = false;
        console.error(err);
        this.tenantFetchError = 'Error getting tenant details';
      },
    });
  }

  initializeData(tenant: Tenant) {
    this.first = tenant.first;
    this.last = tenant.last;
    this.title = tenant.title;
    this.company = tenant.company;
    this.address = tenant.address;
    this.address2 = tenant.address2;
    this.city = tenant.city;
    this.st = tenant.st;
    this.zip = tenant.zip;
    this.billingSameAsAddress = tenant.billing_address === tenant.address;
    this.billingAddress = tenant.billing_address;
    this.billingAddress2 = tenant.billing_address2;
    this.billingCity = tenant.billing_city;
    this.billingSt = tenant.billing_st;
    this.billingZip = tenant.billing_zip;

    this.phone1 = tenant.phone1;
    this.phone2 = tenant.phone2;
    this.emails = tenant.emails;
    this.billingEmails = tenant.billing_emails || [];
  }

  handleBillingSameAsAddressChecked(ev: Event) {
    const checked = (ev.target as HTMLInputElement).checked;
    if (checked) {
      this.billingAddress = this.address;
      this.billingAddress2 = this.address2;
      this.billingCity = this.city;
      this.billingSt = this.st;
      this.billingZip = this.zip;
    } else {
      this.billingAddress = '';
      this.billingAddress2 = '';
      this.billingCity = '';
      this.billingSt = '';
      this.billingZip = '';
    }
  }

  addEmail() {
    if (!this.newEmail) {
      return;
    }

    if (!this.validateEmail(this.newEmail)) {
      this.emailError = 'Invalid email';
      return;
    }

    if (this.emails.includes(this.newEmail)) {
      this.emailError = 'Email already added';
      return;
    }

    this.emails.push(this.newEmail);
    this.newEmail = '';
    this.emailError = '';
  }

  removeEmail(email: string) {
    this.emails = this.emails.filter((e) => e !== email);
  }

  addBillingEmail() {
    if (!this.newBillingEmail) {
      return;
    }
    if (!this.validateEmail(this.newBillingEmail)) {
      this.billingEmailError = 'Invalid email';
      return;
    }

    if (this.billingEmails.includes(this.newBillingEmail)) {
      this.billingEmailError = 'Email already added';
      return;
    }

    this.billingEmails.push(this.newBillingEmail);
    this.newBillingEmail = '';
    this.billingEmailError = '';
  }

  removeBillingEmail(email: string) {
    this.billingEmails = this.billingEmails.filter((e) => e !== email);
  }

  previousStep() {
    this.currentStep--;
  }

  nextStep() {
    this.currentStep++;
  }

  submit() {
    console.log('submitting form');

    const request: UpdateTenantRequest = {
      first: this.first,
      last: this.last,
      title: this.title,
      company: this.company,
      address: this.address,
      address2: this.address2,
      city: this.city,
      st: this.st,
      zip: this.zip,
      phone1: this.phone1,
      phone2: this.phone2,
      emails: this.emails,
      billing_emails: this.billingEmails,
      billing_address: '',
      billing_address2: '',
      billing_city: '',
      billing_st: '',
      billing_zip: '',
    };

    if (this.billingSameAsAddress) {
      request.billing_address = this.address;
      request.billing_address2 = this.address2;
      request.billing_city = this.city;
      request.billing_st = this.st;
      request.billing_zip = this.zip;
    } else {
      request.billing_address = this.billingAddress;
      request.billing_address2 = this.billingAddress2;
      request.billing_city = this.billingCity;
      request.billing_st = this.billingSt;
      request.billing_zip = this.billingZip;
    }

    console.log('request: ', request);

    const tenantId = this.authService.currentUser?.tenant_id;
    if (!tenantId) {
      console.error(new Error('No tenant ID'));
      return;
    }

    console.log('tenantId: ', tenantId);

    this.tenantsService.updateTenant(tenantId, request).subscribe({
      next: (response) => {
        console.log('response: ', response);
        // this.response = JSON.stringify(response);
      },
      error: (error) => {
        console.error(error);
        this.error = JSON.stringify(error);
      },
    });
  }

  handlePhoneChange(ev: Event, num: number) {
    const phone = (ev.target as HTMLInputElement).value;
    const digits = phone.replace(/\D/g, '');

    if (digits.length === 0) {
      if (num === 1) {
        this.phone1 = '';
        this.phone1Error = '';
      } else {
        this.phone2 = '';
        this.phone2Error = '';
      }
      return;
    }

    if (digits.length !== 10) {
      console.error(new Error('Phone number must have 10 digits'));
      if (num === 1) {
        this.phone1Error = 'Phone number must have 10 digits';
      } else {
        this.phone2Error = 'Phone number must have 10 digits';
      }
      return;
    }

    const newPhone = `(${digits.slice(0, 3)}) ${digits.slice(
      3,
      6
    )}-${digits.slice(6)}`;

    if (num === 1) {
      this.phone1 = newPhone;
    } else {
      this.phone2 = newPhone;
    }
  }

  validateEmail(email: string) {
    console.log('validating email: ', email);
    // email regex
    const re = /\S+@\S+\.\S+/;
    if (!re.test(email)) {
      console.error(new Error('Invalid email'));
      return false;
    }

    return true;
  }
}
